import {
  EntertainmentFooter,
  InternetFooter,
  MobileFooter,
  PrepaidFooter,
} from "@/components/BentoBox/components/TabsBentoContainer/FooterComponents";
import IconInternetEssentials from "@/components/icons/IconInternetEssentials";
import IconUberEats from "@/components/icons/IconUberEats";
import IconXfinityPro from "@/components/icons/IconXfinityPro";
import entertainmentStreamSaverBG from "@/images/bento-boxes/entertainment-purple-background.jpg";
import entertainmentStreamSaver from "@/images/bento-boxes/entertainment-streamsaver-movie-collage.png";

import galaxyZFold8 from "@/images/bento-boxes/galaxy-zfold8-light-bg.jpg";
import googlePixel11 from "@/images/bento-boxes/google-pixel-11.jpg";
import iphone18ProMaxDark from "@/images/bento-boxes/iphone-18-pro-max-dark.jpg";
import iphone18Pro from "@/images/bento-boxes/iphone-18-pro.jpg";
import nowStreamSaver from "@/images/bento-boxes/now-stream-saver.png";
import intelligentWifiBento from "@/images/bento-boxes/internet-intelligent-wifi-bento.jpg";
import studentWifiBento from "@/images/bento-boxes/student-wifi-bedroom-bento.jpg";
import wifiShieldBento from "@/images/bento-boxes/wifi-shield-bento.jpg";
import xfinityProBentoDesktop from "@/images/bento-boxes/xfinity-pro-patio-bento--desktop.jpg";
import xfinityProBentoMobile from "@/images/bento-boxes/xfinity-pro-patio-bento--mobile.jpg";
import {
  buildXfinityClickOffUrl,
  PLAN_BUILDER_INTERNET_URL,
} from "@/lib/clickthrough-destination";
const PLAN_BUILDER_LOCALIZED_URL = buildXfinityClickOffUrl(
  "/planbuilder?localize=true&drawer=internet"
);
import type { BentoCardProps, TabData, TabsBentoData } from "@/types/tabsBento";
import React from "react";

const XfinityProText = () =>
  React.createElement("span", {
    className: "text-white title-sm-semibold",
    dangerouslySetInnerHTML: {
      __html: "Xfinity Pro",
    },
  });

export const BADGE_ICONS = {
  "xfinity-pro": IconXfinityPro,
  "xfinity-pro-text": XfinityProText,
  "uber-eats": IconUberEats,
  "internet-essentials": IconInternetEssentials,
  "now-xfinity":
    "https://assets.xfinity.com/assets/dotcom/projects_3/2025/may/cix-8745_expanded-lob-merchandising-on-homepage-and-hsd-learn/prepaid/NOW-Logo.svg",
} as const;

// Internet tab cards
const internetTabCards: BentoCardProps[] = [
  {
    title: React.createElement("span", {
      className: "text-theme1-base",
      dangerouslySetInnerHTML: {
        __html: "Imagine the most intelligent WiFi.&nbsp;Only with Xfinity.",
      },
    }),
    description:
      "Included with internet plans, the Xfinity Gateway powers all your shows, games, and endless scrolls. On one speedy, reliable network.",
    backgroundImage: intelligentWifiBento.src,
    mobileBackgroundImage: intelligentWifiBento.src,
    theme: "light",
    minHeight: "626px",
    tabletMinHeight: "626px",
    mobileMinHeight: "560px",
    padding: "3.5rem 2rem",
    mobilePadding: "2rem 1rem",
    descriptionClassName: "mb-6",
    buttonContainerClassName: "mb-4",
    elementName: "WifiEquipment",
    contentWidth: "full",
    buttons: [
      {
        text: "Shop internet",
        action: "shop-internet",
        tagularProps: {
          actionOutcome: "sentToLocalize",
        },
        href: `${PLAN_BUILDER_INTERNET_URL}&intcmp=ILC:NA:ACQ:BAUP:PRO:GEN:HIS:COM:DIG:DigitalBentoInternet:801`,
        display: "fill",
        tone: "neutral-dark",
        size: "xs",
      },
    ],
  },
  {
    title: "Expansive coverage. Built-in backup. Just $15/mo.",
    description:
      "Extend your WiFi range and stay connected during an outage with Xfinity Pro.",
    backgroundImage: xfinityProBentoDesktop.src,
    mobileBackgroundImage: xfinityProBentoMobile.src,
    theme: "dark",
    minHeight: "626px",
    tabletMinHeight: "626px",
    mobileMinHeight: "560px",
    padding: "3.5rem 2rem",
    mobilePadding: "2rem 1rem",
    descriptionClassName: "mb-6",
    buttonContainerClassName: "mb-4",
    elementName: "XfinityPro",
    contentWidth: "full",
    badge: "xfinity-pro-text",
    buttons: [
      {
        text: "Learn about Xfinity Pro",
        action: "learn-xfinity-pro",
        tagularProps: {
          actionOutcome: "externalLink",
        },
        href: "https://www.xfinity.com/learn/internet-service/xpro?intcmp=ILC:NA:ACQ:BAUP:PRO:GEN:HIS:COM:DIG:DigitalBentoHSDXPro:724",
        display: "fill",
        tone: "neutral-light",
        size: "xs",
      },
    ],
  },
  {
    title: "Student WiFi packed with perks",
    description:
      "Go for Gig WiFi and get a $200 prepaid Mastercard, just for students. Plus, Peacock Premium for 2 years.",
    backgroundImage: studentWifiBento.src,
    mobileBackgroundImage: studentWifiBento.src,
    theme: "dark",
    minHeight: "626px",
    tabletMinHeight: "626px",
    mobileMinHeight: "560px",
    padding: "3.5rem 2rem",
    mobilePadding: "2rem 1rem",
    descriptionClassName: "mb-6",
    buttonContainerClassName: "mb-4",
    elementName: "StudentOffer",
    contentWidth: "full",
    buttons: [
      {
        text: "See student offers",
        action: "see-student-offers",
        tagularProps: {
          actionOutcome: "externalLink",
        },
        href: "https://www.xfinity.com/student",
        display: "fill",
        tone: "neutral-light",
        size: "xs",
      },
      {
        text: "Pricing & other info",
        action: "pricing-info-student",
        tagularProps: {
          actionOutcome: "modalOpened",
        },
        display: "ghost",
        tone: "inverse",
        size: "sm",
        className: "underline",
      },
    ],
  },
  {
    title: "WiFi with built-in protection",
    description:
      "Your Xfinity internet includes WiFi Shield features to keep you informed. Enjoy WiFi Motion, Xfinity CyberSecure, and more — no extra cost or equipment.",
    backgroundImage: wifiShieldBento.src,
    mobileBackgroundImage: wifiShieldBento.src,
    theme: "dark",
    minHeight: "626px",
    tabletMinHeight: "626px",
    mobileMinHeight: "560px",
    padding: "3.5rem 2rem",
    mobilePadding: "2rem 1rem",
    descriptionClassName: "mb-6",
    buttonContainerClassName: "mb-4",
    elementName: "Essentials",
    badge: undefined,
    contentWidth: "full",
    buttons: [
      {
        text: "Shop Internet",
        action: "shop-internet",
        tagularProps: {
          actionOutcome: "externalLink",
        },
        href: `${PLAN_BUILDER_INTERNET_URL}&intcmp=ILC:NA:ACQ:BAUP:PRO:GEN:HIS:COM:DIG:DigitalBentoInternet:801`,
        display: "fill",
        tone: "neutral-light",
        size: "xs",
      },
    ],
    legalText:
      "Restrictions apply. Not avail. in all areas. Xfinity Internet & compatible equip. req.",
  },
  {
    title: "Reliable home internet for under $15/mo",
    description: "Includes unlimited data and equipment. See if you qualify.",
    className: "bg-brand",
    theme: "dark",
    minHeight: "626px",
    tabletMinHeight: "626px",
    mobileMinHeight: "560px",
    padding: "3.5rem 2rem",
    mobilePadding: "2rem 1rem",
    descriptionClassName: "mb-6",
    buttonContainerClassName: "mb-4",
    elementName: "InternetEssentials",
    badge: "internet-essentials",
    badgeClassName: "w-30 h-10",
    contentWidth: "full",
    buttons: [
      {
        text: "Check eligibility",
        action: "check-eligibility-internet-essentials",
        tagularProps: {
          actionOutcome: "externalLink",
        },
        href: "https://apply.internetessentials.com",
        display: "fill",
        tone: "neutral-light",
        size: "xs",
      },
    ],
    legalText:
      "Restrictions apply. Not available in all areas. Limited to residential customers meeting certain eligibility criteria. Subject to Internet Essentials program terms and conditions. Advertised price applies to a single outlet. Actual speeds vary and are not guaranteed.",
  },
];

// Prepaid tab cards
const prepaidTabCards: BentoCardProps[] = [
  {
    title: "All-in monthly prices on internet, mobile, and TV",
    description: "No hidden fees. Unlimited data. A network that works.",
    badge: "now-xfinity",
    backgroundImage:
      "https://assets.xfinity.com/assets/dotcom/projects_3/2025/may/cix-8745_expanded-lob-merchandising-on-homepage-and-hsd-learn/prepaid/LOBTab-Prepaid_NOWIntro_Desktop.jpg",
    mobileBackgroundImage:
      "https://assets.xfinity.com/assets/dotcom/projects_3/2025/may/cix-8745_expanded-lob-merchandising-on-homepage-and-hsd-learn/prepaid/LOBTab-Prepaid_NOWIntro_Mobile.jpg",
    theme: "light",
    className: "col-span-8",
    gridRow: "1",
    gridColumn: "1 / 9",
    minHeight: "400px",
    tabletMinHeight: "560px",
    mobileMinHeight: "560px",
    elementName: "NowXfinity",
    padding: "2rem",
    contentWidth: "500",
    contentAlignment: "center",
    mobileContentAlignment: "top",
  },
  {
    subtitle: "NOW Internet",
    title: `Internet starting\nat $30/mo`,
    backgroundImage:
      "https://assets.xfinity.com/assets/dotcom/projects_3/2025/may/cix-8745_expanded-lob-merchandising-on-homepage-and-hsd-learn/prepaid/LOBTab-Prepaid_NOWInternet_Desktop.jpg",
    mobileBackgroundImage:
      "https://assets.xfinity.com/assets/dotcom/projects_3/2025/may/cix-8745_expanded-lob-merchandising-on-homepage-and-hsd-learn/prepaid/LOBTab-Prepaid_NOWInternet_Mobile.jpg",
    theme: "dark",
    className: "col-span-4",
    gridRow: "1",
    gridColumn: "9 / 13",
    minHeight: "400px",
    tabletMinHeight: "560px",
    mobileMinHeight: "560px",
    padding: "2rem",
    elementName: "NowInternet",
    buttons: [
      {
        text: "Shop NOW Internet",
        action: "shop-now-internet",
        tagularProps: {
          actionOutcome: "externalLink",
        },
        href: "https://www.xfinity.com/now/internet?intcmp=ILC:NA:ACQ:BAUP:PRO:GEN:NOWI:COM:DIG:DigitalBentoNOWHSD:748",
        display: "fill",
        tone: "neutral-light",
        size: "sm",
      },
    ],
  },
  {
    subtitle: "NOW Mobile",
    title: `No contract,\nno credit check`,
    backgroundImage:
      "https://assets.xfinity.com/assets/dotcom/projects_3/2025/may/cix-8745_expanded-lob-merchandising-on-homepage-and-hsd-learn/prepaid/LOBTab-Prepaid_NOWMobile_Desktop.jpg",
    mobileBackgroundImage:
      "https://assets.xfinity.com/assets/dotcom/projects_3/2025/may/cix-8745_expanded-lob-merchandising-on-homepage-and-hsd-learn/prepaid/LOBTab-Prepaid_NOWMobile_Mobile.jpg",
    theme: "light",
    className: "col-span-4",
    gridRow: "2",
    gridColumn: "1 / 5",
    minHeight: "400px",
    tabletMinHeight: "560px",
    mobileMinHeight: "560px",
    padding: "2rem",
    elementName: "NowMobile",
    buttons: [
      {
        text: "Shop NOW Mobile",
        action: "shop-now-mobile",
        tagularProps: {
          actionOutcome: "externalLink",
        },
        href: "https://www.xfinity.com/now/mobile?intcmp=ILC:NA:ACQ:BAUP:PRO:GEN:NOWM:COM:DIG:DigitalBentoNOWM:751",
        display: "fill",
        tone: "neutral-dark",
        size: "sm",
      },
    ],
  },
  {
    subtitle: "NOW TV + Streaming",
    title: `Entertainment starting\nat $10/mo`,
    backgroundImage:
      "https://assets.xfinity.com/assets/dotcom/projects_3/2025/may/cix-8745_expanded-lob-merchandising-on-homepage-and-hsd-learn/prepaid/LOBTab-Prepaid_NOWTV_Desktop-new.jpg",
    mobileBackgroundImage:
      "https://assets.xfinity.com/assets/dotcom/projects_3/2025/may/cix-8745_expanded-lob-merchandising-on-homepage-and-hsd-learn/prepaid/LOBTab-Prepaid_NOWTV_Mobile-new.jpg",
    theme: "dark",
    className: "col-span-4",
    gridRow: "2",
    gridColumn: "5 / 9",
    minHeight: "400px",
    tabletMinHeight: "560px",
    mobileMinHeight: "560px",
    padding: "2rem",
    elementName: "NowTV",
    buttons: [
      {
        text: "Shop NOW TV + Streaming",
        action: "shop-now-tv",
        tagularProps: {
          actionOutcome: "externalLink",
        },
        href: "https://www.xfinity.com/now/tv-streaming?intcmp=ILC:NA:ACQ:BAUP:PRO:GEN:NOWTV:COM:DIG:DigitalBentoNOWTVS:754",
        display: "fill",
        tone: "neutral-light",
        size: "sm",
      },
    ],
  },
  {
    subtitle: "NOW WiFi Pass",
    title: "Access millions of hot spots, $10/mo",
    backgroundImage:
      "https://assets.xfinity.com/assets/dotcom/projects_3/2025/may/cix-8745_expanded-lob-merchandising-on-homepage-and-hsd-learn/prepaid/LOBTab-Prepaid_NOWWiFiPass_Desktop.jpg",
    mobileBackgroundImage:
      "https://assets.xfinity.com/assets/dotcom/projects_3/2025/may/cix-8745_expanded-lob-merchandising-on-homepage-and-hsd-learn/prepaid/LOBTab-Prepaid_NOWWiFiPass_Mobile.jpg",
    theme: "light",
    className: "col-span-4",
    gridRow: "2",
    gridColumn: "9 / 13",
    minHeight: "400px",
    tabletMinHeight: "560px",
    mobileMinHeight: "560px",
    padding: "2rem",
    elementName: "NowWiFiPass",
    buttons: [
      {
        text: "Shop WiFi Pass",
        action: "shop-wifi-pass",
        tagularProps: {
          actionOutcome: "externalLink",
        },
        href: "https://www.xfinity.com/learn/internet-service/wifi?intcmp=ILC:NA:ACQ:BAUP:PRO:GEN:NOWWFP:COM:DIG:DigitalBentoNWP:757",
        display: "fill",
        tone: "neutral-dark",
        size: "sm",
      },
    ],
  },
];

// Entertainment tab cards
const entertainmentTabCards: BentoCardProps[] = [
  {
    title: "Pick your favorite app bundle and save over 25%",
    titleClassName: "patch-text-headline3",
    description:
      "Discover a simple way to save on your favorite apps with a StreamSaver™ bundle, starting at $18/mo. Keep your profiles and watch history from your current app subscriptions.",
    className: "rounded-lg",
    customContent: React.createElement("img", {
      src: entertainmentStreamSaver.src,
      alt: "Purple background with images of streaming content from various apps",
      className:
        "max-w-full max-h-full w-auto h-auto object-contain object-bottom mt-auto",
    }),
    theme: "dark",
    backgroundImage: entertainmentStreamSaverBG.src,
    mobileBackgroundImage: entertainmentStreamSaverBG.src,
    minHeight: "699px",
    tabletMinHeight: "628px",
    mobileMinHeight: "574px",
    padding: "0",
    useCustomPadding: true,
    contentWidth: "full",
    elementName: "StreamSaver",
    buttons: [
      {
        text: "Build your plan",
        action: "shop-streamsaver",
        tagularProps: {
          actionOutcome: "sentToLocalize",
        },
        href: PLAN_BUILDER_LOCALIZED_URL,
        display: "fill",
        tone: "inverse",
        size: "sm",
      },
    ],
  },
  {
    title: "270+ channels for $20/mo",
    titleClassName: "patch-text-headline3",
    description:
      "Get NOW TV with 270+ live and on demand channels, 20 hours of cloud DVR, & Peacock Premium with ads.",
    customContent: React.createElement("img", {
      src: "https://assets.xfinity.com/assets/dotcom/projects_3/2025/july/cix-9044_streamstore_launch---cross-merch/NOWTV-Mobile_Image.png",
      alt: "NOW TV entertainment",
      className: "w-full h-auto object-contain object-bottom mt-auto",
    }),
    className: "rounded-lg",
    theme: "light",
    backgroundImage:
      "https://assets.xfinity.com/assets/dotcom/projects_3/2025/july/cix-9044_streamstore_launch---cross-merch/Desktop-Card3.png",
    mobileBackgroundImage:
      "https://assets.xfinity.com/assets/dotcom/projects_3/2025/july/cix-9044_streamstore_launch---cross-merch/Desktop-Card3.png",
    minHeight: "699px",
    tabletMinHeight: "628px",
    mobileMinHeight: "574px",
    padding: "0",
    useCustomPadding: true,
    contentWidth: "full",
    elementName: "NOWTV",
    buttons: [
      {
        text: "Shop NOW TV",
        action: "shop-now-tv",
        tagularProps: {
          actionOutcome: "sentToLocalize",
        },
        href: PLAN_BUILDER_LOCALIZED_URL,
        display: "fill",
        tone: "neutral-dark",
        size: "sm",
      },
      {
        text: "Pricing & other info",
        action: "pricing-info-now-tv",
        tagularProps: {
          actionOutcome: "modalOpened",
        },
        display: "ghost",
        tone: "neutral",
        size: "sm",
        className: "underline",
      },
    ],
  },
  {
    title: "Get it all for $35/mo with NOW StreamSaver™",
    titleClassName: "patch-text-headline3",
    description:
      "Live TV, on demand, and your favorite streaming apps — together for less. Includes Peacock Premium with ads, Netflix Standard with ads, Apple TV, and HBO Max Basic With Ads.",
    customContent: React.createElement("img", {
      src: nowStreamSaver.src,
      alt: "NOW StreamSaver content",
      className: "w-full h-auto object-contain object-bottom mt-auto",
    }),
    className: "rounded-lg",
    theme: "light",
    backgroundImage:
      "https://assets.xfinity.com/assets/dotcom/projects_3/2025/july/cix-9044_streamstore_launch---cross-merch/Desktop-Card4.png",
    mobileBackgroundImage:
      "https://assets.xfinity.com/assets/dotcom/projects_3/2025/july/cix-9044_streamstore_launch---cross-merch/Desktop-Card4.png",
    minHeight: "699px",
    tabletMinHeight: "628px",
    mobileMinHeight: "574px",
    padding: "0",
    useCustomPadding: true,
    contentWidth: "full",
    elementName: "NOWStreamSaver",
    buttons: [
      {
        text: "Shop NOW StreamSaver",
        action: "shop-now-streamsaver",
        tagularProps: {
          actionOutcome: "sentToLocalize",
        },
        href: PLAN_BUILDER_LOCALIZED_URL,
        display: "fill",
        tone: "neutral-dark",
        size: "sm",
      },
      {
        text: "Pricing & other info",
        action: "pricing-info-now-streamsaver",
        tagularProps: {
          actionOutcome: "modalOpened",
        },
        display: "ghost",
        tone: "neutral",
        size: "sm",
        className: "underline",
      },
    ],
  },
  {
    title: "Sports, news, and novelas. Only $15/mo.",
    titleClassName: "patch-text-headline3",
    description:
      "Get NOW TV Latino with 200+ live channels, including over 45 channels in Spanish, Peacock Premium with ads, and ViX Premium with ads.",
    customContent: React.createElement("img", {
      src: "https://assets.xfinity.com/assets/dotcom/projects_3/2025/july/cix-9044_streamstore_launch---cross-merch/NOW-TV_Icons_Mobile.png",
      alt: "NOW TV Latino content",
      className: "w-full h-auto object-contain object-bottom mt-auto",
    }),
    className: "rounded-lg",
    theme: "dark",
    backgroundImage:
      "https://assets.xfinity.com/assets/dotcom/projects_3/2025/july/cix-9044_streamstore_launch---cross-merch/Desktop-Card5.png",
    mobileBackgroundImage:
      "https://assets.xfinity.com/assets/dotcom/projects_3/2025/july/cix-9044_streamstore_launch---cross-merch/Desktop-Card5.png",
    minHeight: "699px",
    tabletMinHeight: "628px",
    mobileMinHeight: "574px",
    padding: "0",
    useCustomPadding: true,
    contentWidth: "full",
    elementName: "NOWTVLatino",
    buttons: [
      {
        text: "Shop NOW TV Latino",
        action: "shop-now-tv-latino",
        tagularProps: {
          actionOutcome: "sentToLocalize",
        },
        href: PLAN_BUILDER_LOCALIZED_URL,
        display: "fill",
        tone: "neutral-light",
        size: "sm",
      },
      {
        text: "Pricing & other info",
        action: "pricing-info-now-tv-latino",
        tagularProps: {
          actionOutcome: "modalOpened",
        },
        display: "ghost",
        tone: "inverse",
        size: "sm",
        className: "underline",
      },
    ],
  },
];

const mobile3CardWideTop: BentoCardProps[] = [
  {
    title: "Save up to $930 on your choice of iPhone",
    titleClassName: "patch-text-headline3",
    descriptionClassName: "mb-4",
    description:
      "Requires new Mobile Plus line, qualifying trade-in, and device payment plan. Offer ends 11/17.",
    theme: "dark",
    backgroundImage: iphone18ProMaxDark.src,
    mobileBackgroundImage: iphone18ProMaxDark.src,
    elementName: "BentoIphone930Off",
    padding: "1.5rem",
    minHeight: "482px",
    tabletMinHeight: "482px",
    mobileMinHeight: "482px",
    buttons: [
      {
        text: "Pricing & other info",
        action: "pricing-info-bento-iphone-930",
        tagularProps: {
          actionOutcome: "modalOpened",
        },
        display: "ghost",
        tone: "inverse",
        size: "sm",
        className: "underline",
      },
    ],
    bottomCta: {
      text: "Shop iPhone 18 Pro Max",
      action: "shop-iphone-18-pro-max",
      tagularProps: {
        actionOutcome: "externalLink",
      },
      href: buildXfinityClickOffUrl(
        "/digital/mobile/cell-phones/apple-iphone-18-pro-max"
      ),
      display: "fill",
      tone: "neutral-light",
      contentType: "icon",
      iconTheme: "black",
      size: "sm",
      className: "absolute bottom-6 right-6 z-10 p-2 rounded-full",
    },
  },
  {
    title: "$400 off a new iPhone, no trade-in needed",
    titleClassName: "patch-text-headline3",
    descriptionClassName: "mb-4",
    description:
      "New Mobile Plus line and device payment plan required. Offer ends 11/17.",
    theme: "light",
    backgroundImage: iphone18Pro.src,
    mobileBackgroundImage: iphone18Pro.src,
    elementName: "BentoIphone400NoTradeIn",
    padding: "1.5rem",
    minHeight: "482px",
    tabletMinHeight: "482px",
    mobileMinHeight: "482px",
    buttons: [
      {
        text: "Pricing & other info",
        action: "pricing-info-bento-iphone-400-no-trade-in",
        tagularProps: {
          actionOutcome: "modalOpened",
        },
        display: "ghost",
        tone: "neutral",
        size: "sm",
        className: "underline",
      },
    ],
    bottomCta: {
      text: "Shop iPhone 18 Pro",
      action: "shop-iphone-18-pro",
      tagularProps: {
        actionOutcome: "externalLink",
      },
      href: buildXfinityClickOffUrl("/digital/mobile/cell-phones/brand/apple"),
      display: "fill",
      tone: "theme1",
      size: "sm",
      contentType: "icon",
      className: "absolute bottom-6 right-6 z-10 p-2 rounded-full",
    },
  },
  {
    title: "Get up to $1,200 off the latest Samsung Galaxy phones",
    titleClassName: "patch-text-headline3",
    descriptionClassName: "mb-4",
    description:
      "New Mobile Plus line, qualifying trade-in, and device payment plan required. Offer ends 11/1.",
    theme: "light",
    backgroundImage: galaxyZFold8.src,
    mobileBackgroundImage: galaxyZFold8.src,
    elementName: "BentoSamsung1200Off",
    padding: "1.5rem",
    minHeight: "482px",
    tabletMinHeight: "482px",
    mobileMinHeight: "482px",
    buttons: [
      {
        text: "Pricing & other info",
        action: "pricing-info-bento-samsung-1200",
        tagularProps: {
          actionOutcome: "modalOpened",
        },
        display: "ghost",
        tone: "neutral",
        size: "sm",
        className: "underline",
      },
    ],
    bottomCta: {
      text: "Shop Samsung",
      action: "shop-samsung",
      tagularProps: {
        actionOutcome: "externalLink",
      },
      href: buildXfinityClickOffUrl(
        "/digital/mobile/cell-phones/brand/samsung"
      ),
      display: "fill",
      tone: "theme1",
      contentType: "icon",
      size: "sm",
      className: "absolute bottom-6 right-6 z-10 p-2 rounded-full",
    },
  },
  {
    title: "Get the new Google Pixel 11 on us",
    titleClassName: "patch-text-headline3",
    descriptionClassName: "mb-4",
    description:
      "Qualifying trade-in, new Mobile Plus line, and device payment plan required. Offer ends 11/17.",
    theme: "light",
    backgroundImage: googlePixel11.src,
    mobileBackgroundImage: googlePixel11.src,
    elementName: "BentoGooglePixel11OnUs",
    padding: "1.5rem",
    minHeight: "482px",
    tabletMinHeight: "482px",
    mobileMinHeight: "482px",
    buttons: [
      {
        text: "Pricing & other info",
        action: "pricing-info-bento-pixel-11-on-us",
        tagularProps: {
          actionOutcome: "modalOpened",
        },
        display: "ghost",
        tone: "neutral",
        size: "sm",
        className: "underline",
      },
    ],
    bottomCta: {
      text: "Shop Google",
      action: "shop-google",
      tagularProps: {
        actionOutcome: "externalLink",
      },
      href: buildXfinityClickOffUrl("/digital/mobile/cell-phones/pixel-11"),
      display: "fill",
      tone: "theme1",
      contentType: "icon",
      size: "sm",
      className: "absolute bottom-6 right-6 z-10 p-2 rounded-full",
    },
  },
];

export const mobileBentoContent: TabData = {
  id: "mobile",
  label: "Mobile",
  cards: mobile3CardWideTop,
  footer: React.createElement(MobileFooter),
  layoutType: "4-cards-equal",
  sliderClassName: "px-6 md:px-8",
  sliderOverflowStyle: "hide",
};

export const internetBentoContent: TabData = {
  id: "internet",
  label: "Internet",
  cards: internetTabCards,
  footer: React.createElement(InternetFooter),
  layoutType: "custom",
  alwaysCarousel: true,
  // Fractional counts let the track fill the container while the next card
  // peeks in. At the widest container these land just under the per-breakpoint
  // card maximums of 436 (tablet) and 416 (desktop).
  slidesPerViewByBreakpoint: { 0: 1.1, 768: 2.25, 1025: 3.25 },
  // `.content-container` caps mobile content at 400px, which would still let a
  // single peeking card reach ~361px on a landscape phone. Capping the track at
  // 1.1 slides plus the gap holds the card at its 327px mobile maximum; above
  // the tablet breakpoint the track fills the container again.
  sliderClassName: "mx-auto max-w-90.5 md:max-w-none",
  sliderOverflowStyle: "visible",
};

export const entertainmentBentoContent: TabData = {
  id: "entertainment",
  label: "Entertainment",
  cards: entertainmentTabCards,
  footer: React.createElement(EntertainmentFooter),
  layoutType: "3-cards-equal",
  gridClass: "gap-6",
  sliderOverflowStyle: "fade",
};

export const prepaidBentoContent: TabData = {
  id: "prepaid",
  label: "Prepaid",
  cards: prepaidTabCards,
  footer: React.createElement(PrepaidFooter),
  sliderClassName: "md:px-8",
};

const tabsData: TabData[] = [
  mobileBentoContent,
  internetBentoContent,
  entertainmentBentoContent,
  prepaidBentoContent,
];

export const TABS_BENTO_METADATA: TabsBentoData = {
  sectionTitle: "Get more with Xfinity",
  sectionSubtitle: "",
  tabs: tabsData,
};

export const TABS_DEFAULT_ORDER = tabsData.map((tab) => tab.id);

export const TABS_BENTO_VARIANTS = {
  default: TABS_BENTO_METADATA,
};
